import { Suspense, useCallback, useEffect, useRef } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { graphql, useFragment } from "react-relay"
import { X } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Drawer, DrawerBody, DrawerContent, DrawerHeader } from "@/components/ui/drawer"
import { useClickOutside } from "@/hooks/useClickOutside"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { toast } from "@/hooks/useToast"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { AppointmentDrawerCardHeader } from "@/modules/common/waiting-room/appointment/appointment-drawer-card-header"
import { AppointmentDrawerCardHeaderReviewStatus } from "@/modules/common/waiting-room/appointment/appointment-drawer-card-header-review-status"
import { AppointmentDrawerQuestionnaire } from "@/modules/common/waiting-room/appointment/appointment-drawer-questionnaire"
import { AppointmentDrawerStatusIndicator } from "@/modules/common/waiting-room/appointment/appointment-drawer-status-indicator"
import { agendaAppointmentDrawer_appointment$key } from "@/modules/patients/agenda/teleconsultation/agenda-appointment-drawer/__generated__/agendaAppointmentDrawer_appointment.graphql"
import { agendaAppointmentDrawerData_appointment$key } from "@/modules/patients/agenda/teleconsultation/agenda-appointment-drawer/__generated__/agendaAppointmentDrawerData_appointment.graphql"
import { agendaAppointmentDrawerPatient_appointment$key } from "@/modules/patients/agenda/teleconsultation/agenda-appointment-drawer/__generated__/agendaAppointmentDrawerPatient_appointment.graphql"
import { MissedCall } from "@/modules/patients/agenda/teleconsultation/agenda-appointment-drawer/missed-call"
import { AttachmentList } from "@/modules/patients/components/attachments/attachment-list"
import { CallLineBanner } from "@/modules/patients/components/call-line-banner"
import { GeneralHealthInformationSummary } from "@/modules/patients/detail/progress-note/components/general-health-information-summary"
import { RequestedMedicationsSummary } from "@/modules/patients/detail/progress-note/components/requested-medications-summary"
import { VitalSignsSummary } from "@/modules/patients/detail/progress-note/components/vital-signs-summary"
import { usePatientAttachmentSubscription } from "@/modules/patients/services/usePatientAttachmentSubscription"
import { ErrorFallback } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-error-fallback"
import { RequestUploadFilesContainer } from "@/modules/waiting-room/components/request-upload-files-container"
import { useAgendaAppointmentByIdQuery } from "@/modules/waiting-room/services/useAgendaAppointmentById"
import { SofiaEnvironment } from "@/sofia-environment"

const AgendaAppointmentDrawerPatientFragment = graphql`
  fragment agendaAppointmentDrawerPatient_appointment on Appointment {
    patient {
      contactChannel
      isContactChannelVerified
    }
  }
`

const AgendaAppointmentDrawerDataFragment = graphql`
  fragment agendaAppointmentDrawerData_appointment on Appointment {
    id
    reason
    status
    questionnaireId
    telephoneLine {
      telephoneLineTranslations {
        language
        content
      }
    }
    appointmentAttachments {
      id
      url
      thumbnailUrl
      uploadDate
      attachmentType
      fileName
      uploadStatus
      uploadSource
    }
    fileUploadRequest {
      ...FileUploadRequestFragment
    }
    ...appointmentDrawerCardHeader_appointment
    healthOverview {
      vitalSign {
        ...VitalSignFragment
      }
      generalHealthInformation {
        ...GeneralHealthInformationFragment
      }
    }
    ...RequestedMedicationsFragment
    ...appointmentDrawerCardHeaderReviewStatus_appointment
    ...appointmentDrawerQuestionnaire_appointment
    ...appointmentDrawerStatusIndicator_appointment
    ...missedCallAgendaAppointmentDrawerDataFragment
  }
`

const AgendaAppointmentDrawerFragment = graphql`
  fragment agendaAppointmentDrawer_appointment on Appointment {
    ...agendaAppointmentDrawerPatient_appointment
    ...agendaAppointmentDrawerData_appointment
  }
`

type AgendaAppointmentDrawerContentProps = {
  onClose?: () => void
  appointmentRef: agendaAppointmentDrawer_appointment$key
}

const AgendaAppointmentDrawerContent = ({ appointmentRef, onClose }: AgendaAppointmentDrawerContentProps) => {
  const agendaAppointmentDrawerFragmentReference = useFragment<agendaAppointmentDrawer_appointment$key>(
    AgendaAppointmentDrawerFragment,
    appointmentRef,
  )

  const currentLanguage = useCurrentLanguage()

  const agendaAppointmentData = useFragment<agendaAppointmentDrawerData_appointment$key>(
    AgendaAppointmentDrawerDataFragment,
    agendaAppointmentDrawerFragmentReference,
  )
  const { id, questionnaireId, reason, status, appointmentAttachments, telephoneLine, fileUploadRequest } =
    agendaAppointmentData

  const { patient: agendaAppointmentDrawerPatientData } = useFragment<agendaAppointmentDrawerPatient_appointment$key>(
    AgendaAppointmentDrawerPatientFragment,
    agendaAppointmentDrawerFragmentReference,
  )

  const attachments = appointmentAttachments
    .filter((attachment) => attachment.uploadSource !== "DOC2_U")
    .map((attachment) => ({
      ...attachment,
      url: `${SofiaEnvironment.Bff.Url}/${attachment.url}`,
      thumbnailUrl: attachment.thumbnailUrl ? `${SofiaEnvironment.Bff.Url}/${attachment.thumbnailUrl}` : null,
    }))

  const mapTelephoneLineContent = telephoneLine?.telephoneLineTranslations.find(
    (translation) => currentLanguage.toUpperCase() === convertLanguageToISOCode(translation.language),
  )?.content

  usePatientAttachmentSubscription(id)

  return (
    <>
      <DrawerHeader className="animate-fade-in-bottom flex flex-col gap-4" data-testid="agenda-appointment-drawer-head">
        <Button
          data-testid="close-agenda-appointment-drawer-icon"
          onClick={onClose}
          variant="circle"
          className="h-10 w-10 shrink-0 self-end p-2">
          <X />
        </Button>
      </DrawerHeader>
      <DrawerBody data-testid="agenda-appointment-drawer-body">
        <div className="flex flex-col items-start justify-start gap-4">
          <div
            key={id}
            className="animate-fade-in-top border-border bg-surface text-on-surface-subdued grid w-full content-start gap-4 rounded-xl border p-4 text-sm">
            <div className="flex items-center justify-between">
              <AppointmentDrawerCardHeader appointmentRef={agendaAppointmentData} />
              <div className="relative left-2 flex items-center gap-2">
                <AppointmentDrawerCardHeaderReviewStatus appointmentRef={agendaAppointmentData} />
              </div>
            </div>
            <p data-testid="appointment-reason">{reason}</p>
            {agendaAppointmentData.healthOverview?.vitalSign && (
              <VitalSignsSummary vitalSignKey={agendaAppointmentData.healthOverview.vitalSign} />
            )}
            {agendaAppointmentData.healthOverview?.generalHealthInformation && (
              <GeneralHealthInformationSummary
                generalHealthInformationKey={agendaAppointmentData.healthOverview.generalHealthInformation}
              />
            )}
            <RequestedMedicationsSummary appointmentRef={agendaAppointmentData} />
            {questionnaireId && <AppointmentDrawerQuestionnaire appointmentRef={agendaAppointmentData} />}
            {attachments.length !== 0 && <AttachmentList mediaFiles={attachments} appointmentId={id} />}
            {telephoneLine && mapTelephoneLineContent && (
              <CallLineBanner bannerInfo={mapTelephoneLineContent} testId="appointment-drawer-call-line-banner" />
            )}
            {!["CLOSED", "CANCELLED"].includes(status) && (
              <Suspense fallback={null}>
                <RequestUploadFilesContainer
                  appointmentId={id}
                  contactChannel={agendaAppointmentDrawerPatientData.contactChannel}
                  isContactChannelVerified={agendaAppointmentDrawerPatientData.isContactChannelVerified}
                  fileUploadRequestRef={fileUploadRequest}
                />
              </Suspense>
            )}
            <div className="flex flex-wrap gap-4 pt-4">
              <AppointmentDrawerStatusIndicator appointmentRef={agendaAppointmentData} />
            </div>
          </div>
          <MissedCall appointmentRef={agendaAppointmentData} />
        </div>
      </DrawerBody>
    </>
  )
}

const AgendaAppointmentDrawer = ({ selectedAppointmentId }: { selectedAppointmentId: string }) => {
  const drawerRef = useRef<HTMLDivElement>(null)
  const hasAppointment = !!selectedAppointmentId

  useClickOutside((event: MouseEvent) => {
    if (drawerRef.current && !drawerRef.current.contains(event.target as HTMLElement)) {
      closeDrawer()
    }
  }, drawerRef)

  const closeDrawer = () => {
    handleToggleDrawer(true)
  }

  const handleToggleDrawer = useCallback(
    (close = false) => {
      if (drawerRef.current) {
        if (close) {
          drawerRef.current.classList.remove("w-125")
          drawerRef.current.classList.add("hidden", "w-0")
        } else {
          drawerRef.current.classList.add("w-125")
          drawerRef.current.classList.remove("hidden", "w-0")
        }
      }
    },
    [drawerRef],
  )

  return (
    <Drawer open>
      <div className="duration-300">
        {hasAppointment && (
          <DrawerContent
            className="shadow-1 absolute right-0 z-12 border"
            ref={drawerRef}
            onOpenAutoFocus={(e) => e.preventDefault()}>
            <ErrorBoundary fallback={<ErrorFallback onClose={closeDrawer} />}>
              <Suspense>
                <AgendaAppointmentDrawerChecker
                  onClose={closeDrawer}
                  onToggleDrawer={handleToggleDrawer}
                  selectedAppointmentId={selectedAppointmentId}
                />
              </Suspense>
            </ErrorBoundary>
          </DrawerContent>
        )}
      </div>
    </Drawer>
  )
}

type AgendaAppointmentDrawerCheckerProps = {
  onClose?: () => void
  selectedAppointmentId: string
  onToggleDrawer?: (close?: boolean) => void
}

const AgendaAppointmentDrawerChecker = ({
  selectedAppointmentId,
  onClose,
  onToggleDrawer,
}: AgendaAppointmentDrawerCheckerProps) => {
  const appointmentRef = useAgendaAppointmentByIdQuery(selectedAppointmentId)
  const { t } = useTranslation()

  useEffect(() => {
    if (!appointmentRef) {
      toast({ description: t("scheduling.waitingRoom.appointmentUnavailable") })
      onToggleDrawer?.(true)
    } else {
      onToggleDrawer?.()
    }
  }, [appointmentRef, t, onToggleDrawer])

  if (!appointmentRef) {
    return null
  }

  return (
    <AgendaAppointmentDrawerContent key={selectedAppointmentId} onClose={onClose} appointmentRef={appointmentRef} />
  )
}

export { AgendaAppointmentDrawer }
